(() => {
  const root = window.Shopify?.routes?.root || '/';
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[char]));

  const formatMoney = (cents, currency = window.Shopify?.currency?.active || 'USD') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency, maximumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2
      }).format(Number(cents) / 100);
    } catch (_) {
      return `$${(Number(cents) / 100).toFixed(2)}`;
    }
  };

  const signatureDressNames = {
    lc6127918: 'The Camille Cotton Floral Midi',
    lc6128017: 'The Rosalie Scalloped Floral Dress',
    lc7861243: 'The Audrey Cotton Denim Midi',
    lc6128087: 'The Florence Garden Floral Midi',
    lc6127959: 'The Margot Rose Floral Dress',
    lc6128015: 'The Vivienne Midnight Floral Maxi',
    lc6128725: 'The Charlotte Riviera Stripe Midi',
    lc6128316: 'The Isabelle Blue Botanical Maxi',
    lc6127248: 'The Amelia Autumn Floral Dress',
    lc6127426: 'The Juliette Green Stripe Dress',
    lc6126757: 'The Sienna Leopard Midi',
    lc6128753: 'The Josephine Botanical Resort Maxi'
  };

  const brandedDressName = (item) => {
    const source = `${item.sku || ''} ${item.url || ''} ${item.product_title || ''}`.toLowerCase();
    const matchedCode = Object.keys(signatureDressNames).find((code) => source.includes(code));
    if (matchedCode) return signatureDressNames[matchedCode];
    return String(item.product_title || 'Mira Rowe dress').replace(/^(Dear[- ]?Lover|DearLover)\s+/i, '');
  };

  const renderBundleProgress = (itemCount) => {
    const stage = Math.min(itemCount, 3);
    const title = 'Your second and third dresses get an extra 50% off.';
    let copy = 'Your extra savings are applied automatically at checkout, with shipping and 7-day returns.';
    let link = '';

    if (itemCount === 1) {
      copy = 'Add a second dress for an extra 50% off, then a third for an extra 50% off too. The offer applies to the lower-priced styles—even when already reduced.';
      link = `<a href="${root}collections/all">Choose your second dress →</a>`;
    } else if (itemCount === 2) {
      copy = 'Add a third eligible dress for an extra 50% off too. The offer applies to your lower-priced second and third dresses—even when already reduced.';
      link = `<a href="${root}collections/all">Choose your third dress →</a>`;
    }

    return `<div class="bundle-progress bundle-progress-stage-${stage}">
      <div class="bundle-progress-track" aria-hidden="true"><span></span></div>
      <strong>${title}</strong><p>${copy}</p>${link}
    </div>`;
  };

  const menuButton = qs('[data-menu-toggle]');
  const mainNav = qs('[data-main-nav]');
  if (menuButton && mainNav) {
    menuButton.addEventListener('click', () => {
      const open = mainNav.classList.toggle('open');
      menuButton.setAttribute('aria-expanded', String(open));
      menuButton.textContent = open ? 'Close' : 'Menu';
    });
  }

  const drawer = qs('[data-cart-drawer]');
  const cartContent = qs('[data-cart-content]');
  const setDrawer = (open) => {
    if (!drawer) return;
    drawer.hidden = !open;
    document.body.classList.toggle('drawer-open', open);
    qsa('[data-cart-open]').forEach((button) => button.setAttribute('aria-expanded', String(open)));
  };
  qsa('[data-cart-open]').forEach((button) => button.addEventListener('click', () => setDrawer(true)));
  qsa('[data-cart-close]').forEach((button) => button.addEventListener('click', () => setDrawer(false)));
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') setDrawer(false); });

  const renderCart = (cart) => {
    qsa('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; });
    if (!cartContent) return;

    if (!cart.item_count) {
      cartContent.innerHTML = `<div class="empty-cart"><p>Your bag is waiting for something beautiful.</p><p>Your second and third dresses each get an extra 50% off—even when already reduced—with shipping and 7-day returns.</p><a class="text-link" href="${root}collections/all">Shop our dresses →</a></div>`;
      return;
    }

    const items = cart.items.map((item) => {
      const displayName = brandedDressName(item);
      return `
      <article class="cart-item" data-line-key="${escapeHtml(item.key)}">
        ${item.image ? `<img src="${escapeHtml(item.image)}&width=220" alt="${escapeHtml(displayName)}">` : ''}
        <div>
          <h3>${escapeHtml(displayName)}</h3>
          <p>${escapeHtml(item.variant_title || '')} · Qty ${item.quantity}</p>
          <p>${formatMoney(item.final_line_price, cart.currency)}</p>
          <button type="button" data-cart-remove="${escapeHtml(item.key)}">Remove</button>
        </div>
      </article>`;
    }).join('');

    const originalTotal = cart.items.reduce((sum, item) => sum + Number(item.original_line_price || item.final_line_price || 0), 0);
    const promotionalSavings = Math.max(0, originalTotal - Number(cart.total_price || 0));
    const discountLine = promotionalSavings > 0
      ? `<div class="cart-edit-total"><span>Dress offer savings</span><strong>-${formatMoney(promotionalSavings, cart.currency)}</strong></div>`
      : cart.item_count >= 2
        ? '<div class="cart-edit-total"><span>Extra dress offer</span><strong>50% off at checkout</strong></div>'
        : '';

    cartContent.innerHTML = `
      ${renderBundleProgress(cart.item_count)}
      <div class="cart-items">${items}</div>
      <div class="cart-total">
        <div class="cart-line"><span>Subtotal</span><strong>${formatMoney(cart.total_price, cart.currency)}</strong></div>
        ${discountLine}
        <div class="cart-free-shipping"><span>Shipping</span><strong>Free on orders $150+</strong></div>
        <p>Your dress offer savings and taxes are confirmed at checkout.</p>
        <a class="button button-dark" href="${root}cart">Review bag</a>
        <a class="button checkout-button" href="${root}checkout">Continue to checkout</a>
        <small>Secure checkout · Shipping · 7-day returns</small>
      </div>`;
  };

  const refreshCart = async () => {
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to refresh bag');
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };

  if (cartContent) {
    cartContent.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-cart-remove]');
      if (!button) return;
      button.disabled = true;
      try {
        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: button.dataset.cartRemove, quantity: 0 })
        });
        if (!response.ok) throw new Error('Unable to remove item');
        renderCart(await response.json());
      } catch (_) {
        button.disabled = false;
        button.textContent = 'Try again';
      }
    });
  }

  const populateProductFacts = (productRoot) => {
    const template = qs('[data-product-details-source]', productRoot);
    if (!template) return;

    const holder = document.createElement('div');
    holder.appendChild(template.content.cloneNode(true));
    const normalize = (value) => String(value || '').replace(/\s+/g, ' ').trim();
    const lines = qsa('li, p', holder).map((node) => normalize(node.textContent)).filter(Boolean);
    const fullText = normalize(holder.textContent);

    const findFact = (labels) => {
      for (const line of lines) {
        for (const label of labels) {
          const pattern = new RegExp(`^${label}\\s*:\\s*(.+)$`, 'i');
          const match = line.match(pattern);
          if (match?.[1]) return normalize(match[1]).slice(0, 220);
        }
      }

      const boundary = '(?=\\s*(?:Features|Stretch|Body|Lining|Material composition|Fabric|Care instructions|Care|Product measurements|Size and fit)\\s*:|$)';
      for (const label of labels) {
        const match = fullText.match(new RegExp(`${label}\\s*:\\s*(.+?)${boundary}`, 'i'));
        if (match?.[1]) return normalize(match[1]).slice(0, 220);
      }
      return '';
    };

    const composition = findFact(['Material composition', 'Fabric composition', 'Fabric', 'Material']);
    const care = findFact(['Care instructions', 'Care']);
    const lining = findFact(['Body', 'Lining']);
    const stretch = findFact(['Stretch']);

    const compositionNode = qs('[data-fabric-composition]', productRoot);
    const careNode = qs('[data-fabric-care]', productRoot);
    const fabricDetail = qs('[data-fabric-detail]', productRoot);
    // Never replace a product's manually configured or already detected fabric.
    if (compositionNode && !compositionNode.textContent.trim() && composition) {
      compositionNode.textContent = composition;
      compositionNode.hidden = false;
    }
    if (careNode && care) {
      careNode.textContent = `Care: ${care}`;
      careNode.hidden = false;
    }
    if (fabricDetail && ((compositionNode && compositionNode.textContent.trim()) || care || !fabricDetail.hidden)) fabricDetail.hidden = false;

    const liningNode = qs('[data-fabric-lining]', productRoot);
    const stretchNode = qs('[data-fabric-stretch]', productRoot);
    const constructionDetail = qs('[data-construction-detail]', productRoot);
    if (liningNode && lining) liningNode.textContent = `Lining: ${lining}`;
    if (stretchNode && stretch) stretchNode.textContent = `Stretch: ${stretch}`;
    if (constructionDetail && (lining || stretch)) constructionDetail.hidden = false;
  };

  qsa('[data-product-root]').forEach((productRoot) => {
    populateProductFacts(productRoot);

    const gallery = qs('[data-product-gallery]', productRoot);
    if (gallery) {
      const track = qs('[data-product-gallery-track]', gallery);
      const slides = track ? qsa('figure', track) : [];
      const dots = qsa('[data-gallery-dot]', gallery);
      const current = qs('[data-gallery-current]', gallery);

      if (track && slides.length > 1 && dots.length) {
        let scrollFrame = 0;

        const setActiveSlide = (index) => {
          const activeIndex = Math.max(0, Math.min(index, slides.length - 1));
          dots.forEach((dot, dotIndex) => {
            const selected = dotIndex === activeIndex;
            dot.classList.toggle('is-active', selected);
            dot.setAttribute('aria-current', String(selected));
          });
          if (current) current.textContent = String(activeIndex + 1);
        };

        const updateActiveSlide = () => {
          if (scrollFrame) return;
          scrollFrame = window.requestAnimationFrame(() => {
            scrollFrame = 0;
            const activeIndex = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
            setActiveSlide(activeIndex);
          });
        };

        dots.forEach((dot, index) => {
          dot.addEventListener('click', () => {
            track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
          });
        });

        track.addEventListener('scroll', updateActiveSlide, { passive: true });
        setActiveSlide(0);
      }
    }

    const form = qs('[data-product-form]', productRoot);
    const variantData = qs('[data-product-variants]', productRoot);
    if (!form || !variantData) return;

    let variants = [];
    try { variants = JSON.parse(variantData.textContent); } catch (_) { return; }

    const updateVariant = () => {
      const values = qsa('.variant-group', form).map((group) => qs('input:checked', group)?.value).filter(Boolean);
      const variant = variants.find((candidate) => candidate.options.every((value, index) => value === values[index])) || variants[0];
      if (!variant) return;
      qs('[data-variant-id]', form).value = variant.id;
      const button = qs('[data-add-to-cart]', form);
      const label = qs('[data-add-label]', form);
      const price = qs('[data-add-price]', form);
      const discount = qs('[data-add-discount]', form);
      const pagePrice = qs('[data-product-price]', productRoot);
      const pageComparePrice = qs('[data-product-compare-price]', productRoot);
      const pageSaving = qs('[data-product-saving]', productRoot);
      button.disabled = !variant.available;
      label.textContent = variant.available ? 'Add to bag' : 'Sold out';
      price.textContent = formatMoney(variant.price);
      if (pagePrice) pagePrice.textContent = formatMoney(variant.price);
      const compareAtPrice = Number(variant.compare_at_price || 0);
      const savings = compareAtPrice - Number(variant.price || 0);
      const isReduced = compareAtPrice > Number(variant.price || 0);
      if (pageComparePrice) {
        pageComparePrice.hidden = !isReduced;
        pageComparePrice.textContent = isReduced ? formatMoney(compareAtPrice) : '';
      }
      if (pageSaving) {
        pageSaving.hidden = !isReduced;
        pageSaving.textContent = isReduced
          ? `You save ${formatMoney(savings)} today · ${Math.round((savings / compareAtPrice) * 100)}% off`
          : '';
      }
      if (discount) {
        discount.hidden = !isReduced;
        discount.textContent = isReduced
          ? ` · Save ${Math.round((savings / compareAtPrice) * 100)}%`
          : '';
      }
    };

    form.addEventListener('change', updateVariant);
    updateVariant();

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = qs('[data-add-to-cart]', form);
      const label = qs('[data-add-label]', form);
      if (button.disabled) return;
      const original = label.textContent;
      button.disabled = true;
      label.textContent = 'Adding…';
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        if (!response.ok) throw new Error('Unable to add dress');
        await refreshCart();
        setDrawer(true);
        label.textContent = 'Added';
        window.setTimeout(() => { label.textContent = original; button.disabled = false; }, 800);
      } catch (_) {
        label.textContent = 'Please try again';
        button.disabled = false;
      }
    });
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-quick-add-variant]');
    if (!button) return;
    event.preventDefault();
    if (button.disabled || button.dataset.quickAddBusy === 'true') return;

    const variantId = Number(button.dataset.quickAddVariant);
    if (!variantId) return;

    const originalLabel = button.dataset.quickAddDefaultLabel || button.textContent.trim();
    const originalMarkup = button.innerHTML;
    button.dataset.quickAddBusy = 'true';
    button.disabled = true;
    button.textContent = 'Adding…';

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      if (!response.ok) throw new Error('Unable to add dress');
      await refreshCart();
      setDrawer(true);
      button.textContent = 'Added';
      const details = button.closest('.product-quick-add');
      if (details) window.setTimeout(() => { details.open = false; }, 500);
      window.setTimeout(() => {
        button.innerHTML = originalMarkup;
        button.disabled = false;
        button.dataset.quickAddBusy = 'false';
      }, 900);
    } catch (_) {
      button.textContent = 'Please try again';
      window.setTimeout(() => {
        button.innerHTML = originalMarkup;
        button.disabled = false;
        button.dataset.quickAddBusy = 'false';
      }, 1200);
    }
  });

  const loadProductRecommendations = async (section) => {
    const url = section.dataset.recommendationsUrl;
    if (!url || section.dataset.recommendationsLoaded === 'true') return;
    section.dataset.recommendationsLoaded = 'true';

    try {
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) return;

      const markup = await response.text();
      const responseDocument = new DOMParser().parseFromString(markup, 'text/html');
      const replacement = qs('[data-product-recommendations]', responseDocument);
      if (!replacement || !qs('.product-card', replacement)) return;

      section.innerHTML = replacement.innerHTML;
      section.hidden = false;
    } catch (_) {
      // Keep the server-rendered collection fallback when recommendations fail.
    }
  };

  qsa('[data-product-recommendations]').forEach((section) => {
    if (section.hidden || !('IntersectionObserver' in window)) {
      loadProductRecommendations(section);
      return;
    }

    const observer = new IntersectionObserver((entries, recommendationObserver) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      recommendationObserver.disconnect();
      loadProductRecommendations(section);
    }, { rootMargin: '280px 0px' });

    observer.observe(section);
  });

})();
